<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Moldes"
      v-model="filtro"
      searchPlaceholder="Buscar por nombre o código..."
    >
      <template #actions>
        <button class="btn btn-nuevo btn-nuevo-header" @click="abrirModal(null)">
          <i class="bi bi-plus-lg"></i>
          <span class="btn-nuevo-texto ms-2">Nuevo molde</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- ── Tabla desktop ── -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
        <p class="text-cafe mt-2">Cargando moldes...</p>
      </div>

      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="nombre"
                label="Nombre del molde"
                :filterable="false"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="codigo_interno"
                label="Código interno"
                :filterable="false"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="estatus_label"
                label="Estatus molde"
                :items="moldesParaFiltro"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                v-model="filtroEstatusSet"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="estado_label"
                label="Estado"
                :items="moldesParaFiltro"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                v-model="filtroEstadoSet"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="molde in moldesPaginados" :key="molde.id">
            <td class="ps-4">
              <span class="fw-bold text-cafe">{{ molde.nombre }}</span>
            </td>
            <td>
              <span class="badge-molde">{{ molde.codigo_interno }}</span>
            </td>
            <td class="text-center">
              <span :class="['badge-estatus-molde', clsEstatus(molde.estatus)]">
                {{ labelEstatus(molde.estatus) }}
              </span>
            </td>
            <td class="text-center">
              <span :class="['badge-estado', molde.activo ? 'activo' : 'inactivo']">
                {{ molde.activo ? "Activo" : "Inactivo" }}
              </span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button class="btn-circular" @click="abrirModal(molde)" title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarBorradoLogico(molde)"
                  title="Desactivar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="moldesPaginados.length === 0">
            <td colspan="5" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron resultados
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="totalPaginas > 1"
        class="pagination-wrapper d-flex justify-content-between align-items-center px-4 py-3"
      >
        <small class="text-cafe fw-bold"
          >Página {{ paginaActual }} de {{ totalPaginas }}</small
        >
        <div class="d-flex gap-2">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="cambiarPagina(paginaActual - 1)"
          >
            <i class="bi bi-chevron-left"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="cambiarPagina(paginaActual + 1)"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Cards móvil ── -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="molde in moldesPaginados"
        :key="'mob-' + molde.id"
        class="divisan-mobile-card mb-2 px-3 py-2 shadow-sm"
      >
        <!-- Fila 1: nombre + botones -->
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="fw-bold text-cafe card-name">{{ molde.nombre }}</span>
          <div class="d-flex gap-2">
            <button class="btn-circular-sm" @click="abrirModal(molde)" title="Editar">
              <i class="bi bi-pencil-square text-verde"></i>
            </button>
            <button
              class="btn-circular-sm"
              @click="confirmarBorradoLogico(molde)"
              title="Desactivar"
            >
              <i class="bi bi-trash3-fill text-danger"></i>
            </button>
          </div>
        </div>
        <!-- Fila 2: código + estatus molde + estado -->
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <span class="badge-molde">{{ molde.codigo_interno }}</span>
          <span :class="['badge-estatus-molde', clsEstatus(molde.estatus)]">
            {{ labelEstatus(molde.estatus) }}
          </span>
          <span :class="['badge-estado', molde.activo ? 'activo' : 'inactivo']">
            {{ molde.activo ? "Activo" : "Inactivo" }}
          </span>
        </div>
      </div>

      <div
        v-if="totalPaginas > 1"
        class="pagination-mobile d-flex justify-content-between align-items-center px-1 py-2 mt-1"
      >
        <button
          class="btn-pag-mobile"
          :disabled="paginaActual === 1"
          @click="cambiarPagina(paginaActual - 1)"
        >
          <i class="bi bi-chevron-left"></i> Anterior
        </button>
        <small class="text-cafe fw-bold">{{ paginaActual }} / {{ totalPaginas }}</small>
        <button
          class="btn-pag-mobile"
          :disabled="paginaActual === totalPaginas"
          @click="cambiarPagina(paginaActual + 1)"
        >
          Siguiente <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- ── Modal crear / editar ── -->
    <div
      class="modal fade"
      id="modalMolde"
      tabindex="-1"
      aria-hidden="true"
      ref="modalElement"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              {{ editando ? "Editar molde" : "Nuevo molde" }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <form @submit.prevent="guardarMolde">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label label-divisan">Nombre del molde</label>
                <input
                  v-model="form.nombre"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. Engorda 1"
                />
              </div>
              <div class="mb-3">
                <label class="form-label label-divisan">Código interno</label>
                <input
                  v-model="form.codigo_interno"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. MLD-001"
                />
              </div>
              <div class="mb-3">
                <label class="form-label label-divisan">Estatus del molde</label>
                <select
                  v-model="form.estatus"
                  class="form-select divisan-input-modal"
                  required
                >
                  <option value="OK">✅ OK — Apto para producción</option>
                  <option value="NO_USAR">🚫 No usar — Obsoleto o dañado</option>
                  <option value="CONDICIONADO">
                    ⚠️ Condicionado — Pendiente de validación
                  </option>
                  <option value="COMBINADO">
                    🔄 Molde combinado — Requiere cambio de insertos
                  </option>
                </select>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                data-bs-dismiss="modal"
              >
                Cancelar
              </button>
              <button type="submit" class="btn btn-nuevo py-2 px-4" :disabled="guardando">
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar cambios" : "Guardar molde" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import {
  getMoldes,
  crearMolde,
  actualizarMolde,
  eliminarMolde,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

// ── Estado ─────────────────────────────────────────────────
const moldes = ref([]);
const filtro = ref("");
const filtroEstatusSet = ref(new Set());
const filtroEstadoSet = ref(new Set());
const loading = ref(false);
const guardando = ref(false);
const editando = ref(false);
const modalElement = ref(null);
const form = ref({ id: null, nombre: "", codigo_interno: "", estatus: "OK" });

// Paginación
const paginaActual = ref(1);
const porPagina = ref(5);
const ordenCampo = ref("nombre");
const ordenAsc = ref(true);

function onSort({ field, asc }) {
  ordenCampo.value = field;
  ordenAsc.value = asc;
}

let modalInstance = null;

// ── Helpers estatus ────────────────────────────────────────
const ESTATUS_MAP = {
  OK: { label: "OK", cls: "ok" },
  NO_USAR: { label: "No usar", cls: "no-usar" },
  CONDICIONADO: { label: "Condicionado", cls: "condicionado" },
  COMBINADO: { label: "Combinado", cls: "combinado" },
};

function labelEstatus(val) {
  if (!val) return "Sin asignar";
  return ESTATUS_MAP[val]?.label ?? val;
}
function clsEstatus(val) {
  if (!val) return "sin-asignar";
  return ESTATUS_MAP[val]?.cls ?? "sin-asignar";
}

// ── Computed ───────────────────────────────────────────────
watch([filtro, filtroEstatusSet, filtroEstadoSet], () => {
  paginaActual.value = 1;
});

const moldesPorTexto = computed(() => {
  const s = filtro.value.toLowerCase();
  if (!s) return moldes.value;
  return moldes.value.filter(
    (m) =>
      m.nombre.toLowerCase().includes(s) || m.codigo_interno.toLowerCase().includes(s)
  );
});

// Universo con etiquetas legibles, para que los filtros de columna
// (Estatus/Estado) muestren y comparen texto amigable en vez de códigos crudos.
const moldesParaFiltro = computed(() =>
  moldesPorTexto.value.map((m) => ({
    ...m,
    estatus_label: labelEstatus(m.estatus),
    estado_label: m.activo ? "Activo" : "Inactivo",
  }))
);

const moldesFiltrados = computed(() =>
  moldesPorTexto.value.filter((m) => {
    const matchEstatus =
      !filtroEstatusSet.value.size || filtroEstatusSet.value.has(labelEstatus(m.estatus));
    const matchEstado =
      !filtroEstadoSet.value.size ||
      filtroEstadoSet.value.has(m.activo ? "Activo" : "Inactivo");
    return matchEstatus && matchEstado;
  })
);

const moldesOrdenados = computed(() => {
  return [...moldesFiltrados.value].sort((a, b) => {
    let valA = a[ordenCampo.value];
    let valB = b[ordenCampo.value];
    if (typeof valA === "string") {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }
    if (valA < valB) return ordenAsc.value ? -1 : 1;
    if (valA > valB) return ordenAsc.value ? 1 : -1;
    return 0;
  });
});

const totalPaginas = computed(() =>
  Math.ceil(moldesOrdenados.value.length / porPagina.value)
);

const moldesPaginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return moldesOrdenados.value.slice(inicio, inicio + porPagina.value);
});

function cambiarPagina(pagina) {
  if (pagina >= 1 && pagina <= totalPaginas.value) paginaActual.value = pagina;
}

// ── Métodos ────────────────────────────────────────────────
async function cargarMoldes() {
  loading.value = true;
  try {
    moldes.value = await getMoldes();
  } catch {
    Swal.fire("Error", "No se pudo conectar con el servidor", "error");
  } finally {
    loading.value = false;
  }
}

function abrirModal(molde = null) {
  if (molde) {
    editando.value = true;
    form.value = { ...molde };
  } else {
    editando.value = false;
    form.value = { id: null, nombre: "", codigo_interno: "", estatus: "OK" };
  }
  if (modalInstance) modalInstance.show();
}

async function guardarMolde() {
  guardando.value = true;
  try {
    if (editando.value) {
      await actualizarMolde(form.value.id, form.value);
    } else {
      await crearMolde(form.value);
    }
    await cargarMoldes();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `Molde ${editando.value ? "actualizado" : "creado"} correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "Ocurrió un problema al procesar la solicitud", "error");
  } finally {
    guardando.value = false;
  }
}

async function confirmarBorradoLogico(molde) {
  const result = await Swal.fire({
    title: `¿Desactivar ${molde.nombre}?`,
    text:
      "El historial permanecerá, pero no se podrán crear nuevos registros con este molde.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "#6c757d",
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
  });
  if (result.isConfirmed) {
    try {
      await eliminarMolde(molde.id);
      await cargarMoldes();
      Swal.fire("Desactivado", "El molde ha sido deshabilitado.", "success");
    } catch {
      Swal.fire("Error", "No se pudo desactivar el molde.", "error");
    }
  }
}

onMounted(() => {
  cargarMoldes();
  if (modalElement.value) {
    modalInstance = new Modal(modalElement.value);
  }
});
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
}
.btn-nuevo:hover {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}

.divisan-card {
  background-color: var(--d-cream);
  border-radius: 12px;
  border-left: 5px solid var(--d-green);
}
.divisan-input {
  background-color: white !important;
  border: 1px solid var(--d-green);
  color: var(--d-brown) !important;
  border-radius: 8px;
}
.search-wrapper {
  position: relative;
}
.search-wrapper i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  z-index: 5;
}
.search-wrapper input {
  padding-left: 35px;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-2);
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 1px solid var(--d-border);
  padding: 15px;
  font-weight: 800;
  text-transform: capitalize;
  border-top: none;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 15px;
}
.table-divisan tbody tr {
  transition: background-color 0.15s ease;
}
.table-divisan tbody tr:hover {
  background-color: var(--d-nav-hover-bg);
}

/* Badge código interno */
.badge-molde {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
  padding: 4px 10px;
  border-radius: 6px;
  font-family: var(--d-font-mono);
  font-size: 0.78rem;
}

.card-name {
  font-size: 0.92rem;
}

/* Badge estatus molde */
.badge-estatus-molde {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 800;
  white-space: nowrap;
}
.badge-estatus-molde.ok {
  background: #2e7d32;
  color: #fff;
}
.badge-estatus-molde.no-usar {
  background: #c62828;
  color: #fff;
}
.badge-estatus-molde.condicionado {
  background: #e65100;
  color: #fff;
}
.badge-estatus-molde.combinado {
  background: #424242;
  color: #fff;
}
.badge-estatus-molde.sin-asignar {
  background: #e0e0e0;
  color: #757575;
  font-style: italic;
}

/* Badge activo/inactivo */
.badge-estado {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
}
.activo {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.inactivo {
  background-color: var(--d-brown);
  color: var(--d-cream);
}

/* Cards móvil */
.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  border-left: 6px solid var(--d-green);
  box-shadow: var(--d-elevation-1);
  transition: box-shadow 0.15s ease;
}
.divisan-mobile-card:active {
  box-shadow: var(--d-elevation-2);
}

/* Botones circulares */
.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
}
.btn-circular-sm {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
  font-size: 0.85rem;
}
.btn-circular-sm:hover {
  background-color: rgba(96, 108, 56, 0.1);
}

/* Modal */
.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}

/* Paginación */
.pagination-wrapper {
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background-color: rgba(96, 108, 56, 0.05);
}
.btn-pag {
  background: white;
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 8px;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-pag:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag:disabled {
  opacity: 0.35;
  cursor: default;
}

.pagination-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

/* Header responsive */
.btn-nuevo-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  /* Solo ícono: el texto sobra para que quepa junto al buscador en un
     renglón (ver CrudToolbar.vue). */
  .btn-nuevo-texto {
    display: none;
  }
  .btn-nuevo-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}
</style>
